import { create } from "zustand";
import {
  Quote,
  TimeEntry,
  Invoice,
  LaborRole,
  Kit,
  PriceListItem,
  Customer,
  TermsTemplate,
  QuoteMode,
  QuoteStatus,
  BidItem,
  PdfExportSettings,
  QuoteHistory,
  ProjectSchedule,
  Subcontractor,
  CompanyInfo,
  AIBrief,
  AIActionProposal,
  AIChatMessage,
  AISettings,
  SchedulingSettings,
} from "../types";
import { QBOSyncConfig, QBOAccountMapping, QBOItemMapping } from "../types/qbo";
import type {
  Receipt,
  ReceiptCostCategory,
  ReceiptTagMapping,
  ReceiptTaxProfile,
} from "../types/receipts";
import { StorageService } from "../services/storage";
import {
  DEMO_QUOTE,
  DEFAULT_KITS,
  DEFAULT_CUSTOMERS,
  LABOR_ROLES,
  DEFAULT_MARKUP,
  DEFAULT_PST_FACTOR,
  DEFAULT_PDF_SETTINGS,
  TERMS_LIBRARY,
  DEFAULT_COMPANY_INFO,
  DEFAULT_VALIDITY_DAYS,
  PRIME_CHECKLIST_TEMPLATE,
  SUB_CHECKLIST_TEMPLATE,
  DEFAULT_SCHEDULING_SETTINGS,
} from "../constants";
import { calculateQuoteTotals } from "../services/calculationService";
import { resetAIService } from "../services/ai";
import { debounce } from "../utils/debounce";
import { APP_CONFIG } from "../constants/app";
import { deepClone } from "../utils/clone";
import { invalidateQuoteCache } from "../services/exportService";
import { generateId, generateCustomId } from "../utils/idGenerator";
import { computeDueList, MaintenanceDueItem } from "../services/maintenance/due";

const mergeQuoteLaborRoles = (
  quoteRoles: LaborRole[] | undefined,
  globalRoles: LaborRole[]
): LaborRole[] => {
  const safeQuoteRoles = Array.isArray(quoteRoles) ? quoteRoles : [];
  const safeGlobalRoles = Array.isArray(globalRoles) ? globalRoles : [];

  // If global roles are not available yet, preserve the quote snapshot.
  if (safeGlobalRoles.length === 0) return safeQuoteRoles;

  const quoteRoleById = new Map(safeQuoteRoles.map((r) => [r.id, r]));
  const globalRoleIds = new Set(safeGlobalRoles.map((r) => r.id));

  // Prefer global role identity (id/name) but preserve quote-specific rates/costs.
  const merged: LaborRole[] = safeGlobalRoles.map((globalRole) => {
    const existing = quoteRoleById.get(globalRole.id);
    if (!existing) return deepClone(globalRole);
    return {
      ...globalRole,
      rate: existing.rate ?? globalRole.rate,
      costRate: existing.costRate ?? globalRole.costRate,
    };
  });

  // Preserve any quote-only roles that aren't in the global list.
  for (const role of safeQuoteRoles) {
    if (!globalRoleIds.has(role.id)) merged.push(role);
  }

  return merged;
};

// Helper to handle storage result and dispatch auth events
const handleStorageResult = (result: { success: boolean; error?: string }) => {
  if (!result.success && result.error === "auth") {
    window.dispatchEvent(new CustomEvent("storage-auth-failure", { detail: result }));
  } else if (result.success) {
    window.dispatchEvent(new CustomEvent("storage-success"));
  }
};

// Debounced storage functions for frequently updated data
const debouncedSaveQuotes = debounce(async (quotes: Quote[]) => {
  const result = await StorageService.saveQuotes(quotes);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const debouncedSaveTimeEntries = debounce(async (entries: TimeEntry[]) => {
  const result = await StorageService.saveTimeEntries(entries);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const debouncedSaveInvoices = debounce(async (invoices: Invoice[]) => {
  const result = await StorageService.saveInvoices(invoices);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const debouncedSaveReceipts = debounce(async (receipts: Receipt[]) => {
  const result = await StorageService.saveReceipts(receipts);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const debouncedSaveReceiptTagMappings = debounce(async (mappings: ReceiptTagMapping[]) => {
  const result = await StorageService.saveReceiptTagMappings(mappings);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const debouncedSaveReceiptCostCategories = debounce(async (categories: ReceiptCostCategory[]) => {
  const result = await StorageService.saveReceiptCostCategories(categories);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const debouncedSaveReceiptTaxProfiles = debounce(async (profiles: ReceiptTaxProfile[]) => {
  const result = await StorageService.saveReceiptTaxProfiles(profiles);
  handleStorageResult(result);
}, APP_CONFIG.DEBOUNCE.STORAGE_DELAY);

const generateQuoteId = (prefix: string, existingIds: Set<string>): string => {
  const now = new Date();
  const yy = now.getFullYear().toString().slice(-2);
  const mm = (now.getMonth() + 1).toString().padStart(2, "0");

  for (let attempt = 0; attempt < 5; attempt += 1) {
    const suffix = generateCustomId("0123456789", 5);
    const candidate = `${prefix}${yy}${mm}-${suffix}`;
    if (!existingIds.has(candidate)) {
      return candidate;
    }
  }

  const suffix = generateCustomId("0123456789", 8);
  return `${prefix}${yy}${mm}-${suffix}`;
};

interface AppState {
  // --- STATE ---
  loading: boolean;
  appMode: "OFFICE" | "FIELD";
  activeQuoteId: string | null;
  theme: "light" | "dark";

  quotes: Quote[];
  timeEntries: TimeEntry[];
  invoices: Invoice[];
  laborRoles: LaborRole[];
  globalSettings: {
    markup: number;
    pst: number;
    validityDays: number;
    pdf: PdfExportSettings;
    defaultContingencyPercent?: number;
  };
  companyInfo: CompanyInfo;
  kits: Kit[];
  priceList: PriceListItem[];
  customers: Customer[];
  subcontractors: Subcontractor[];
  termsTemplates: TermsTemplate[];

  // Receipts (MVP)
  receipts: Receipt[];
  receiptTagMappings: ReceiptTagMapping[];
  receiptCostCategories: ReceiptCostCategory[];
  receiptTaxProfiles: ReceiptTaxProfile[];

  // AI Settings
  aiSettings: AISettings;

  // Scheduling Settings (P1.3)
  schedulingSettings: SchedulingSettings;

  maintenanceDueList: MaintenanceDueItem[];
  maintenanceDueListStatus: "idle" | "computing" | "ready" | "error";
  maintenanceDueListError?: string;
  maintenanceDueListLastComputed?: number;

  // QuickBooks Online Settings
  qboConfig: {
    clientId?: string;
    clientSecret?: string;
    redirectUri?: string;
    environment?: "sandbox" | "production";
    realmId?: string; // Phase 6: QBO company ID (from OAuth callback)
    accountMappings?: QBOAccountMapping[]; // Legacy
    itemMappings?: import("../types/qbo").QBOItemMapping[]; // New - preferred
    autoSyncCustomers: boolean;
    autoSyncInvoices: boolean;
    autoSyncPayments: boolean;
    taxCodeId?: string;
    syncIntervalMinutes?: number;
    depositAccountId?: string; // QBO deposit account for payments
    depositAccountName?: string; // Display name for deposit account
  } | null;

  // AI Office State
  aiBrief: AIBrief | null;
  aiActions: AIActionProposal[];
  aiChatHistory: AIChatMessage[];
  aiActionHistory: Array<{
    action: AIActionProposal;
    executedAt: number;
    result: "success" | "error";
  }>;

  // --- ACTIONS ---

  // Initialization
  loadData: () => Promise<void>;
  setAppMode: (mode: "OFFICE" | "FIELD") => void;
  setActiveQuoteId: (id: string | null) => void;
  setTheme: (theme: "light" | "dark") => void;
  toggleTheme: () => void;

  // Quotes
  createQuote: (data: { mode: QuoteMode; clientName: string; projectName: string }) => string;
  updateQuote: (quote: Quote) => void;
  deleteQuote: (id: string) => void;
  // Atomic actions to prevent race conditions during rapid adds
  addBidItem: (quoteId: string, item: BidItem) => void;
  updateBidItem: (quoteId: string, itemId: string, updates: Partial<BidItem>) => void;
  deleteBidItem: (quoteId: string, itemId: string) => void;
  reorderBidItems: (
    quoteId: string,
    activeItemId: string,
    overItemId: string | null,
    position?: "before" | "after"
  ) => void;

  duplicateQuote: (id: string) => void;
  reviseQuote: (quote: Quote) => void;
  createChangeOrder: (parent: Quote) => void;

  // Time
  addTimeEntry: (entry: TimeEntry) => void;
  deleteTimeEntry: (id: string) => void;

  // Invoices
  addInvoice: (inv: Invoice) => void;
  updateInvoice: (inv: Invoice) => void;
  deleteInvoice: (id: string) => void;

  // Settings & Lists
  updateGlobalSettings: (
    roles: LaborRole[],
    markup: number,
    pst: number,
    pdf: PdfExportSettings,
    companyInfo: CompanyInfo,
    validityDays: number,
    defaultContingencyPercent?: number
  ) => void;
  updateAISettings: (settings: AISettings) => void;
  updateSchedulingSettings: (settings: SchedulingSettings) => void;
  updateQBOConfig: (
    config: Partial<QBOSyncConfig> & {
      clientId?: string;
      clientSecret?: string;
      redirectUri?: string;
      environment?: "sandbox" | "production";
    }
  ) => Promise<void>;
  addCustomer: (c: Customer) => void;
  updateCustomer: (c: Customer) => void;
  deleteCustomer: (id: string) => void;

  addSubcontractor: (s: Subcontractor) => void;
  updateSubcontractor: (s: Subcontractor) => void;
  deleteSubcontractor: (id: string) => void;

  updatePriceList: (items: PriceListItem[]) => void;
  mergePriceList: (items: PriceListItem[]) => void;

  addKit: (kit: Kit) => void;
  deleteKit: (id: string) => void;
  addTermsTemplate: (tmpl: TermsTemplate) => void;

  // Receipts (MVP)
  setReceipts: (receipts: Receipt[]) => void;
  setReceiptTagMappings: (mappings: ReceiptTagMapping[]) => void;
  setReceiptCostCategories: (categories: ReceiptCostCategory[]) => void;
  setReceiptTaxProfiles: (profiles: ReceiptTaxProfile[]) => void;

  computeMaintenanceDue: (opts?: {
    todayYYYYMMDD?: string;
    nowMs?: number;
    ttlMs?: number;
    force?: boolean;
  }) => Promise<void>;
  refreshMaintenanceDue: () => Promise<void>;
  seedMaintenanceDueData: (items: MaintenanceDueItem[]) => void;

  // AI Actions
  setAIBrief: (brief: AIBrief) => void;
  addAIActions: (actions: AIActionProposal[]) => void;
  removeAIAction: (id: string) => void;
  addChatMessage: (msg: AIChatMessage) => void;
  clearChatHistory: () => void;
  addActionHistory: (action: AIActionProposal, result: "success" | "error") => void;
}

export const useStore = create<AppState>((set, get) => ({
  // Defaults
  loading: true,
  appMode: "OFFICE",
  activeQuoteId: null,
  theme: (() => {
    // Load theme from localStorage, default to DARK (industrial SCADA default)
    try {
      const stored = localStorage.getItem("oler_scada_theme");
      return (stored === "LIGHT" ? "light" : "dark") as "light" | "dark";
    } catch {
      return "dark";
    }
  })(),
  quotes: [],
  timeEntries: [],
  invoices: [],
  laborRoles: [],
  globalSettings: {
    markup: DEFAULT_MARKUP,
    pst: DEFAULT_PST_FACTOR,
    validityDays: DEFAULT_VALIDITY_DAYS,
    pdf: DEFAULT_PDF_SETTINGS,
  },
  companyInfo: DEFAULT_COMPANY_INFO,
  kits: [],
  priceList: [],
  customers: [],
  subcontractors: [],
  termsTemplates: [],

  receipts: [],
  receiptTagMappings: [],
  receiptCostCategories: [],
  receiptTaxProfiles: [],

  aiSettings: { provider: "GEMINI", apiKey: "" }, // Default (proxy-only, no client key)

  schedulingSettings: DEFAULT_SCHEDULING_SETTINGS, // P1.3: company-wide scheduling defaults

  qboConfig: null, // Will be loaded from localStorage

  aiBrief: null,
  aiActions: [],
  aiChatHistory: [],
  aiActionHistory: [],
  maintenanceDueList: [],
  maintenanceDueListStatus: "idle",
  maintenanceDueListError: undefined,
  maintenanceDueListLastComputed: undefined,

  // --- IMPLEMENTATION ---

  loadData: async () => {
    set({ loading: true });
    try {
      // Use Promise.allSettled for resilient parallel loading
      // Individual failures don't block other data from loading
      const results = await Promise.allSettled([
        StorageService.getQuotes(), // 0
        StorageService.getTimeEntries(), // 1
        StorageService.getInvoices(), // 2
        StorageService.getLaborRoles(), // 3
        StorageService.getGlobalSettings(), // 4
        StorageService.getCompanyInfo(), // 5
        StorageService.getKits(), // 6
        StorageService.getPriceList(), // 7
        StorageService.getCustomers(), // 8
        StorageService.getSubcontractors(), // 9
        StorageService.getTermsTemplates(), // 10
        StorageService.getAIChatHistory(), // 11
        StorageService.getAIActionHistory(), // 12
        StorageService.getAIActions(), // 13
        StorageService.getReceipts(), // 14
        StorageService.getReceiptTagMappings(), // 15
        StorageService.getReceiptCostCategories(), // 16
        StorageService.getReceiptTaxProfiles(), // 17
      ]);

      // Helper to extract value from settled result with default
      const getValue = <T>(result: PromiseSettledResult<T>, defaultValue: T): T => {
        if (result.status === "fulfilled") {
          return result.value;
        }
        console.warn("Data load failed:", result.reason);
        return defaultValue;
      };

      // Extract values with safe defaults
      const q = getValue(results[0], []);
      const t = getValue(results[1], []);
      const i = getValue(results[2], []);
      const r = getValue(results[3], []);
      const g = getValue(results[4], {});
      const c_info = getValue(results[5], null);
      const k = getValue(results[6], []);
      const p = getValue(results[7], []);
      const c = getValue(results[8], []);
      const s = getValue(results[9], []);
      const terms = getValue(results[10], []);
      const chatHistory = getValue(results[11], []);
      const actionHistory = getValue(results[12], []);
      const aiActions = getValue(results[13], []);
      const receipts = getValue(results[14], []);
      const receiptTagMappings = getValue(results[15], []);
      const receiptCostCategories = getValue(results[16], []);
      const receiptTaxProfiles = getValue(results[17], []);

      // Load AI Settings synchronously from LocalStorage (simple key)
      let aiSettings: AISettings = { provider: "GEMINI", apiKey: "" };
      try {
        const storedAI = localStorage.getItem("oler_ai_settings");
        if (storedAI) {
          const parsed = JSON.parse(storedAI);
          aiSettings = { provider: parsed?.provider || "GEMINI", apiKey: "" };
          if (parsed?.apiKey) {
            localStorage.setItem("oler_ai_settings", JSON.stringify(aiSettings));
          }
        }
      } catch (e) {}

      // Load QBO Config - try API first, then migrate from localStorage if needed
      let qboConfig = null;
      try {
        // First, try to load from D1 via API
        const response = await fetch('/api/settings/qbo', {
          method: 'GET',
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.config && Object.keys(data.config).length > 0) {
            qboConfig = data.config;
          }
        }
        
        // If no config in D1, check localStorage for migration
        if (!qboConfig) {
          const storedQBO = localStorage.getItem("oler_qbo_config");
          if (storedQBO) {
            const localConfig = JSON.parse(storedQBO);
            if (localConfig && Object.keys(localConfig).length > 0) {
              // Migrate to D1
              try {
                const migrateResponse = await fetch('/api/settings/qbo?action=migrate', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ localStorageData: localConfig }),
                });
                
                if (migrateResponse.ok) {
                  const migrateData = await migrateResponse.json();
                  if (migrateData.success) {
                    qboConfig = migrateData.config;
                    // Clear localStorage after successful migration
                    localStorage.removeItem("oler_qbo_config");
                    console.log('QBO config migrated from localStorage to D1');
                  }
                }
              } catch (migrationError) {
                console.warn('QBO config migration failed, using localStorage data:', migrationError);
                qboConfig = localConfig;
              }
            }
          }
        }
      } catch (e) {
        console.warn('Failed to load QBO config from API, will use localStorage fallback:', e);
        // Fallback to localStorage if API fails
        try {
          const storedQBO = localStorage.getItem("oler_qbo_config");
          if (storedQBO) qboConfig = JSON.parse(storedQBO);
        } catch (fallbackError) {
          console.error('Failed to load QBO config from localStorage fallback:', fallbackError);
        }
      }

      // Load Scheduling Settings (P1.3)
      let schedulingSettings: SchedulingSettings = DEFAULT_SCHEDULING_SETTINGS;
      try {
        const storedScheduling = localStorage.getItem("oler_scheduling_settings");
        if (storedScheduling) {
          const parsed = JSON.parse(storedScheduling);
          // Merge with defaults to ensure all fields exist
          schedulingSettings = { ...DEFAULT_SCHEDULING_SETTINGS, ...parsed };
        }
      } catch (e) {}

      // Safety check: ensure arrays are always arrays
      const safeQuotes = Array.isArray(q) ? q : [];
      const safeTimeEntries = Array.isArray(t) ? t : [];
      const safeInvoices = Array.isArray(i) ? i : [];
      const safeCustomers = Array.isArray(c) ? c : [];
      const safeRoles = Array.isArray(r) ? r : [];

      // Ensure quotes always have the latest global labor roles available (additive merge).
      const normalizedQuotes = safeQuotes.map((quote) => ({
        ...quote,
        laborRoles: mergeQuoteLaborRoles(quote.laborRoles, safeRoles),
      }));

      set({
        quotes: normalizedQuotes,
        timeEntries: safeTimeEntries,
        invoices: safeInvoices,
        laborRoles: safeRoles,
        globalSettings: g,
        companyInfo: c_info,
        kits: k,
        priceList: p,
        customers: safeCustomers,
        subcontractors: s,
        termsTemplates: terms,
        receipts: Array.isArray(receipts) ? receipts : [],
        receiptTagMappings: Array.isArray(receiptTagMappings) ? receiptTagMappings : [],
        receiptCostCategories: Array.isArray(receiptCostCategories) ? receiptCostCategories : [],
        receiptTaxProfiles: Array.isArray(receiptTaxProfiles) ? receiptTaxProfiles : [],
        aiSettings: aiSettings,
        schedulingSettings: schedulingSettings,
        qboConfig: qboConfig,
        aiChatHistory: chatHistory || [],
        aiActionHistory: actionHistory || [],
        aiActions: Array.isArray(aiActions) ? aiActions : [],
        loading: false,
      });
      get()
        .refreshMaintenanceDue()
        .catch(() => {});
    } catch (e) {
      console.error("Failed to load store:", e);
      // Ensure we always have valid arrays even on error
      set({
        quotes: [],
        timeEntries: [],
        invoices: [],
        customers: [],
        loading: false,
      });
    }
  },

  setAppMode: (mode) => set({ appMode: mode }),
  setActiveQuoteId: (id) => set({ activeQuoteId: id }),
  setTheme: (theme) => {
    set({ theme });
    try {
      // Use data-theme attribute for CSS variable switching (SCADA styling guide)
      const themeMode = theme === "light" ? "LIGHT" : "DARK";
      document.documentElement.dataset.theme = themeMode;
      localStorage.setItem("oler_scada_theme", themeMode);
      // Also keep class for any remaining Tailwind dark: classes
      document.documentElement.classList.toggle("dark", theme === "dark");
    } catch (e) {
      console.warn("Failed to save theme preference:", e);
    }
  },
  toggleTheme: () => {
    const currentTheme = get().theme;
    const newTheme = currentTheme === "light" ? "dark" : "light";
    get().setTheme(newTheme);
  },

  createQuote: (data) => {
    const { globalSettings, quotes } = get();

    // Generate Smart ID: {PREFIX}{YY}{MM}-{Suffix}
    const prefix =
      data.mode === QuoteMode.SERVICE
        ? APP_CONFIG.ID_PREFIXES.SERVICE
        : APP_CONFIG.ID_PREFIXES.QUOTE;
    const existingIds = new Set(quotes.map((quote) => quote.id));
    const newId = generateQuoteId(prefix, existingIds);

    const template =
      data.mode === QuoteMode.PRIME_FULL ? PRIME_CHECKLIST_TEMPLATE : SUB_CHECKLIST_TEMPLATE;
    const checklist = template.map((item, idx) => ({ ...item, id: `chk-${idx}`, checked: false }));

    const serviceItem: BidItem = {
      id: generateId(),
      code: "01",
      title: "Service Call T&M",
      part: 2,
      laborSteps: [],
      materialLines: [],
      subcontractLines: [],
      shiftHoursPerDay: 8,
      crewSize: 1,
      contingencyPercent: 0,
      notes: "Time & Material Billing",
    };

    const validityDays = globalSettings.validityDays || DEFAULT_VALIDITY_DAYS;

    const newQuote: Quote = {
      ...DEMO_QUOTE,
      id: newId,
      projectName: data.projectName,
      clientName: data.clientName,
      mode: data.mode,
      bidItems: data.mode === QuoteMode.SERVICE ? [serviceItem] : [],
      status: data.mode === QuoteMode.SERVICE ? QuoteStatus.WON : QuoteStatus.DRAFT,
      jobCode: data.mode === QuoteMode.SERVICE ? newId.replace("Q", "S") : undefined,
      dueDate: new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      checklist: checklist,
      primeContractor: data.mode === QuoteMode.SUB_TO_PRIME ? data.clientName : undefined,
      laborRoles: deepClone(get().laborRoles), // Snapshot current roles
      defaultMarkup: globalSettings.markup,
      defaultPst: globalSettings.pst,
      defaultContingencyPercent: globalSettings.defaultContingencyPercent ?? 0.05,
      pdfSettings: { ...globalSettings.pdf },
      documentType: "QUOTE",
      showOutageAnalysis: false,
      enableBidForm: false,
      version: 1,
      revisionHistory: [],
    };

    const newQuotes = [newQuote, ...quotes];
    set({ quotes: newQuotes, activeQuoteId: newId });
    StorageService.saveQuotes(newQuotes).then(handleStorageResult);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        DailyLogActionTracker.logAction("QUOTE_CREATED", {
          id: newId,
          projectName: data.projectName,
          clientName: data.clientName,
        }).catch(console.error);
      })
      .catch(() => {}); // Silently fail if module not available

    return newId;
  },

  updateQuote: (quote) => {
    const oldQuote = get().quotes.find((q) => q.id === quote.id);
    const newQuotes = get().quotes.map((q) => (q.id === quote.id ? quote : q));
    set({ quotes: newQuotes });
    // Save will check for auth failures and dispatch events
    debouncedSaveQuotes(newQuotes);

    // Invalidate PDF cache when quote changes
    invalidateQuoteCache(quote.id);
    get()
      .computeMaintenanceDue()
      .catch(() => {});

    // Log action if status changed
    if (oldQuote && oldQuote.status !== quote.status) {
      import("../services/dailyLogActionTracker")
        .then(({ DailyLogActionTracker }) => {
          DailyLogActionTracker.logAction("QUOTE_UPDATED", {
            id: quote.id,
            projectName: quote.projectName,
            clientName: quote.clientName,
            oldStatus: oldQuote.status,
            newStatus: quote.status,
          }).catch(console.error);
        })
        .catch(() => {});
    }

    // Track learning when quote status changes to WON or SENT
    if (
      oldQuote &&
      oldQuote.status !== quote.status &&
      (quote.status === QuoteStatus.WON || quote.status === QuoteStatus.SENT)
    ) {
      // Import dynamically to avoid circular dependencies
      import("../services/aiLearningService")
        .then(({ AILearningService }) => {
          const state = get();
          AILearningService.extractPatternsFromQuote(
            quote,
            quote.status,
            state.timeEntries, // Pass time entries for profitability calculation
            state.laborRoles // Pass roles for cost calculations
          );
        })
        .catch((err) => {
          console.error("Failed to extract learning patterns:", err);
        });
    }
  },

  deleteQuote: (id) => {
    const quote = get().quotes.find((q) => q.id === id);
    if (!quote) return;

    // Also delete any change orders that reference this quote
    const newQuotes = get().quotes.filter((q) => q.id !== id && q.parentQuoteId !== id);

    // Clear active quote if it's the one being deleted
    const activeQuoteId = get().activeQuoteId;
    if (activeQuoteId === id) {
      set({ quotes: newQuotes, activeQuoteId: null });
    } else {
      set({ quotes: newQuotes });
    }

    // Invalidate PDF cache
    invalidateQuoteCache(id);

    // Save to storage
    StorageService.saveQuotes(newQuotes).then(handleStorageResult);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        DailyLogActionTracker.logAction("QUOTE_DELETED", {
          id: quote.id,
          projectName: quote.projectName,
          clientName: quote.clientName,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  addBidItem: (quoteId, item) => {
    set((state) => {
      const newQuotes = state.quotes.map((q) =>
        q.id === quoteId ? { ...q, bidItems: [...q.bidItems, item] } : q
      );
      debouncedSaveQuotes(newQuotes);
      return { quotes: newQuotes };
    });
  },

  updateBidItem: (quoteId, itemId, updates) => {
    set((state) => {
      const newQuotes = state.quotes.map((q) =>
        q.id === quoteId
          ? {
              ...q,
              bidItems: q.bidItems.map((i) => (i.id === itemId ? { ...i, ...updates } : i)),
            }
          : q
      );
      debouncedSaveQuotes(newQuotes);
      return { quotes: newQuotes };
    });
  },

  deleteBidItem: (quoteId, itemId) => {
    set((state) => {
      const newQuotes = state.quotes.map((q) =>
        q.id === quoteId
          ? {
              ...q,
              bidItems: q.bidItems.filter((i) => i.id !== itemId),
            }
          : q
      );
      StorageService.saveQuotes(newQuotes).then(handleStorageResult);
      return { quotes: newQuotes };
    });
  },

  reorderBidItems: (quoteId, activeItemId, overItemId, position = "before") => {
    set((state) => {
      let didReorder = false;

      const newQuotes = state.quotes.map((q) => {
        if (q.id !== quoteId) return q;

        const items = q.bidItems;
        const fromIndex = items.findIndex((i) => i.id === activeItemId);
        if (fromIndex === -1) return q;

        const overIndex = overItemId ? items.findIndex((i) => i.id === overItemId) : items.length;
        if (overItemId && overIndex === -1) return q;

        const insertionIndex = position === "after" ? overIndex + 1 : overIndex;
        if (!overItemId && insertionIndex === items.length && fromIndex === items.length - 1)
          return q;
        if (overItemId && activeItemId === overItemId) return q;

        const next = [...items];
        const [moved] = next.splice(fromIndex, 1);

        let target = insertionIndex;
        if (fromIndex < target) target -= 1;
        target = Math.max(0, Math.min(next.length, target));

        if (target === fromIndex) return q;

        next.splice(target, 0, moved);
        didReorder = true;
        return { ...q, bidItems: next };
      });

      if (didReorder) debouncedSaveQuotes(newQuotes);
      return didReorder ? { quotes: newQuotes } : {};
    });
  },

  duplicateQuote: (id) => {
    const original = get().quotes.find((q) => q.id === id);
    if (!original) return;

    // Generate new ID
    const existingIds = new Set(get().quotes.map((quote) => quote.id));
    const newId = generateQuoteId(APP_CONFIG.ID_PREFIXES.QUOTE, existingIds);

    const deepCopy = deepClone(original);
    const newQuote: Quote = {
      ...deepCopy,
      id: newId,
      projectName: `${deepCopy.projectName} (Copy)`,
      status: QuoteStatus.DRAFT,
      jobCode: undefined,
      sentDate: undefined,
      schedule: undefined,
      version: 1,
      revisionHistory: [],
      // Regenerate IDs for all sub-objects to prevent reference issues
      bidItems: deepCopy.bidItems.map((item: any) => ({
        ...item,
        id: generateId(),
        laborSteps: item.laborSteps.map((s: any) => ({ ...s, id: generateId() })),
        materialLines: item.materialLines.map((m: any) => ({ ...m, id: generateId() })),
        subcontractLines: item.subcontractLines.map((s: any) => ({ ...s, id: generateId() })),
        actualMaterials: [],
      })),
      checklist: deepCopy.checklist.map((c: any) => ({ ...c, checked: false })),
    };

    const newQuotes = [newQuote, ...get().quotes];
    set({ quotes: newQuotes });
    StorageService.saveQuotes(newQuotes).then(handleStorageResult);
  },

  reviseQuote: (quote) => {
    // 1. Calculate current value for snapshot
    const totals = calculateQuoteTotals(quote, quote.laborRoles);

    // 2. Detect project name change against stored version for audit trail
    const existingQuote = get().quotes.find((q) => q.id === quote.id);
    const nameChanged = existingQuote && existingQuote.projectName !== quote.projectName;
    const snapshotNote = nameChanged
      ? `Previously: ${existingQuote!.projectName}`
      : `Revision created from ${quote.status}`;

    // 3. Create History Entry
    const historyEntry: QuoteHistory = {
      version: quote.version,
      date: new Date().toISOString().split("T")[0],
      status: quote.status,
      totalAmount: totals.grandTotal,
      snapshotNote,
    };

    // 3. Update Quote
    const today = new Date().toISOString().split("T")[0];
    const validityDays = get().globalSettings?.validityDays || DEFAULT_VALIDITY_DAYS;
    const newDueDate = new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    const updatedQuote: Quote = {
      ...quote,
      status: QuoteStatus.DRAFT,
      version: quote.version + 1,
      revisionHistory: [historyEntry, ...(quote.revisionHistory || [])],
      sentDate: undefined,
      createdDate: today,
      dueDate: newDueDate,
    };

    const newQuotes = get().quotes.map((q) => (q.id === quote.id ? updatedQuote : q));
    set({ quotes: newQuotes });
    debouncedSaveQuotes(newQuotes);
  },

  createChangeOrder: (parentQuote) => {
    const coCount = get().quotes.filter((q) => q.parentQuoteId === parentQuote.id).length + 1;
    // CO IDs are ParentID-CO#
    const newId = `${parentQuote.id}-CO${coCount}`;

    const newQuote: Quote = {
      ...parentQuote,
      id: newId,
      projectName: `${parentQuote.projectName} (CO #${coCount})`,
      parentQuoteId: parentQuote.id,
      isChangeOrder: true,
      changeOrderNumber: coCount,
      status: QuoteStatus.DRAFT,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      bidItems: [], // Start with empty scope for CO
      checklist: parentQuote.checklist.map((c) => ({ ...c, checked: false })),
      sentDate: undefined,
      jobCode: undefined,
      version: 1,
      revisionHistory: [],
    };

    const newQuotes = [newQuote, ...get().quotes];
    set({ quotes: newQuotes, activeQuoteId: newId });
    StorageService.saveQuotes(newQuotes).then(handleStorageResult);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        DailyLogActionTracker.logAction("CHANGE_ORDER_CREATED", {
          id: newId,
          projectName: newQuote.projectName,
          changeOrderNumber: coCount,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  addTimeEntry: (entry) => {
    const newEntries = [entry, ...get().timeEntries];
    set({ timeEntries: newEntries });
    debouncedSaveTimeEntries(newEntries);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        const quote = get().quotes.find((q) => q.id === entry.quoteId);
        DailyLogActionTracker.logAction("TIME_ENTRY_LOGGED", {
          id: entry.id,
          hours: entry.hours,
          projectName: quote?.projectName,
          notes: entry.notes,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  deleteTimeEntry: (id) => {
    const entry = get().timeEntries.find((e) => e.id === id);
    const newEntries = get().timeEntries.filter((e) => e.id !== id);
    set({ timeEntries: newEntries });
    debouncedSaveTimeEntries(newEntries);

    // Log action (fire-and-forget)
    if (entry) {
      import("../services/dailyLogActionTracker")
        .then(({ DailyLogActionTracker }) => {
          DailyLogActionTracker.logAction("TIME_ENTRY_DELETED", {
            id: entry.id,
            hours: entry.hours,
          }).catch(console.error);
        })
        .catch(() => {});
    }
  },

  addInvoice: (inv) => {
    const newInvoices = [...get().invoices, inv];
    set({ invoices: newInvoices });
    debouncedSaveInvoices(newInvoices);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        const quote = get().quotes.find((q) => q.id === inv.quoteId);
        DailyLogActionTracker.logAction("INVOICE_CREATED", {
          id: inv.id,
          number: inv.number,
          amount: inv.totalAmount,
          clientName: quote?.clientName,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  updateInvoice: (inv) => {
    const newInvoices = get().invoices.map((i) => (i.id === inv.id ? inv : i));
    set({ invoices: newInvoices });
    debouncedSaveInvoices(newInvoices);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        DailyLogActionTracker.logAction("INVOICE_UPDATED", {
          id: inv.id,
          number: inv.number,
          amount: inv.totalAmount,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  deleteInvoice: (id) => {
    const invoice = get().invoices.find((i) => i.id === id);
    const newInvoices = get().invoices.filter((i) => i.id !== id);
    set({ invoices: newInvoices });
    debouncedSaveInvoices(newInvoices);
    StorageService.deleteInvoice(id)
      .then(handleStorageResult)
      .catch(() => {});

    // Log action (fire-and-forget)
    if (invoice) {
      import("../services/dailyLogActionTracker")
        .then(({ DailyLogActionTracker }) => {
          DailyLogActionTracker.logAction("INVOICE_DELETED", {
            id: invoice.id,
            number: invoice.number,
          }).catch(console.error);
        })
        .catch(() => {});
    }
  },

  updateGlobalSettings: (
    roles,
    markup,
    pst,
    pdf,
    companyInfo,
    validityDays,
    defaultContingencyPercent
  ) => {
    const currentQuotes = get().quotes;
    const normalizedQuotes = currentQuotes.map((quote) => {
      const mergedRoles = mergeQuoteLaborRoles(quote.laborRoles, roles);

      const existingRoles = Array.isArray(quote.laborRoles) ? quote.laborRoles : [];
      const changed =
        mergedRoles.length !== existingRoles.length ||
        mergedRoles.some((r, idx) => {
          const existing = existingRoles[idx];
          return (
            !existing ||
            existing.id !== r.id ||
            existing.name !== r.name ||
            existing.rate !== r.rate ||
            existing.costRate !== r.costRate
          );
        });

      return changed ? { ...quote, laborRoles: mergedRoles } : quote;
    });

    set({
      laborRoles: roles,
      globalSettings: { markup, pst, pdf, validityDays, defaultContingencyPercent },
      companyInfo,
      quotes: normalizedQuotes,
    });
    StorageService.saveLaborRoles(roles);
    StorageService.saveGlobalSettings(
      markup,
      pst,
      pdf,
      validityDays,
      defaultContingencyPercent
    ).then(handleStorageResult);
    StorageService.saveCompanyInfo(companyInfo).then(handleStorageResult);

    // Keep existing quotes in sync with new global roles so active quotes see new roles immediately.
    StorageService.saveQuotes(normalizedQuotes).then(handleStorageResult);
  },

  updateAISettings: (settings) => {
    const sanitized: AISettings = { provider: settings.provider, apiKey: "" };
    set({ aiSettings: sanitized });
    localStorage.setItem("oler_ai_settings", JSON.stringify(sanitized));
    // Force AI service reload
    resetAIService();
  },

  updateSchedulingSettings: (settings) => {
    set({ schedulingSettings: settings });
    localStorage.setItem("oler_scheduling_settings", JSON.stringify(settings));
  },

  updateQBOConfig: async (config) => {
    const current = get().qboConfig || {
      accountMappings: [],
      itemMappings: [],
      autoSyncCustomers: false,
      autoSyncInvoices: false,
      autoSyncPayments: false,
      depositAccountId: undefined,
      depositAccountName: undefined,
    };
    const updated = { ...current, ...config };
    
    // Update state optimistically
    set({ qboConfig: updated });
    
    try {
      // Save to D1 via API
      const response = await fetch('/api/settings/qbo', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save QBO config: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to save QBO config to database');
      }
      
      // Update state with the saved data (in case the server modified anything)
      if (data.config) {
        set({ qboConfig: data.config });
      }
      
    } catch (error) {
      console.error('Failed to save QBO config to API, falling back to localStorage:', error);
      
      // Fallback to localStorage if API fails
      try {
        localStorage.setItem("oler_qbo_config", JSON.stringify(updated));
      } catch (localError) {
        console.error('Failed to save QBO config to localStorage fallback:', localError);
        // Revert state if all saves failed
        set({ qboConfig: current });
        throw error;
      }
    }
  },

  addCustomer: (c) => {
    const newCustomers = [c, ...get().customers];
    set({ customers: newCustomers });
    StorageService.saveCustomers(newCustomers);
  },

  updateCustomer: (c) => {
    const newCustomers = get().customers.map((cust) => (cust.id === c.id ? c : cust));
    set({ customers: newCustomers });
    StorageService.saveCustomers(newCustomers);
  },

  deleteCustomer: (id) => {
    const newCustomers = get().customers.filter((c) => c.id !== id);
    set({ customers: newCustomers });
    StorageService.saveCustomers(newCustomers);
    // Also explicitly delete from D1 (saveCustomers only upserts, doesn't delete)
    StorageService.deleteCustomer(id);
  },

  addSubcontractor: (s) => {
    const newSubs = [s, ...get().subcontractors];
    set({ subcontractors: newSubs });
    StorageService.saveSubcontractors(newSubs);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        DailyLogActionTracker.logAction("SUBCONTRACTOR_ADDED", {
          name: s.name,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  updateSubcontractor: (s) => {
    const newSubs = get().subcontractors.map((sub) => (sub.id === s.id ? s : sub));
    set({ subcontractors: newSubs });
    StorageService.saveSubcontractors(newSubs);

    // Log action (fire-and-forget)
    import("../services/dailyLogActionTracker")
      .then(({ DailyLogActionTracker }) => {
        DailyLogActionTracker.logAction("SUBCONTRACTOR_UPDATED", {
          name: s.name,
        }).catch(console.error);
      })
      .catch(() => {});
  },

  deleteSubcontractor: (id) => {
    const newSubs = get().subcontractors.filter((s) => s.id !== id);
    set({ subcontractors: newSubs });
    StorageService.saveSubcontractors(newSubs);
  },

  updatePriceList: (items) => {
    set({ priceList: items });

    // Auto-update kits if models match
    const newKits = get().kits.map((kit) => {
      let kitUpdated = false;
      const newMaterialLines = kit.materialLines.map((line) => {
        if (!line.modelNumber) return line;
        const match = items.find(
          (p) => p.modelNumber.toLowerCase() === line.modelNumber?.toLowerCase()
        );
        if (match) {
          kitUpdated = true;
          return {
            ...line,
            unitCost: match.unitCost,
            description: match.description || line.description,
            vendor: match.vendor || line.vendor,
          };
        }
        return line;
      });
      return kitUpdated ? { ...kit, materialLines: newMaterialLines } : kit;
    });

    set({ kits: newKits });
    StorageService.savePriceList(items);
    StorageService.saveKits(newKits);
  },

  mergePriceList: (itemsToMerge) => {
    const currentList = [...get().priceList];
    let updated = false;

    itemsToMerge.forEach((newItem) => {
      if (!newItem.modelNumber) return;

      const index = currentList.findIndex(
        (p) => p.modelNumber.toLowerCase() === newItem.modelNumber.toLowerCase()
      );

      if (index >= 0) {
        currentList[index] = {
          ...currentList[index],
          unitCost: newItem.unitCost,
          updatedAt: new Date().toISOString().split("T")[0],
          vendor: currentList[index].vendor || newItem.vendor,
        };
        updated = true;
      } else {
        currentList.push({
          ...newItem,
          updatedAt: new Date().toISOString().split("T")[0],
        });
        updated = true;
      }
    });

    if (updated) {
      get().updatePriceList(currentList);
    }
  },

  addKit: (kit) => {
    const newKits = [...get().kits, kit];
    set({ kits: newKits });
    StorageService.saveKits(newKits);
  },

  deleteKit: (id) => {
    const newKits = get().kits.filter((k) => k.id !== id);
    set({ kits: newKits });
    StorageService.saveKits(newKits);
  },

  addTermsTemplate: (tmpl) => {
    const newTerms = [...get().termsTemplates, tmpl];
    set({ termsTemplates: newTerms });
    StorageService.saveTermsTemplates(newTerms);
  },

  setReceipts: (receipts) => {
    set({ receipts });
    debouncedSaveReceipts(receipts);
  },

  setReceiptTagMappings: (mappings) => {
    set({ receiptTagMappings: mappings });
    debouncedSaveReceiptTagMappings(mappings);
  },

  setReceiptCostCategories: (categories) => {
    set({ receiptCostCategories: categories });
    debouncedSaveReceiptCostCategories(categories);
  },

  setReceiptTaxProfiles: (profiles) => {
    set({ receiptTaxProfiles: profiles });
    debouncedSaveReceiptTaxProfiles(profiles);
  },

  // AI Store Actions
  setAIBrief: (brief) => set({ aiBrief: brief }),

  addAIActions: (actions) => {
    set((state) => {
      const next = [...state.aiActions, ...actions];
      StorageService.saveAIActions(next).catch((err) =>
        console.error("Failed to save AI actions", err)
      );
      return { aiActions: next };
    });
  },

  removeAIAction: (id) => {
    set((state) => {
      const next = state.aiActions.filter((a) => a.id !== id);
      StorageService.saveAIActions(next).catch((err) =>
        console.error("Failed to save AI actions", err)
      );
      return { aiActions: next };
    });
  },

  addChatMessage: (msg) => {
    set((state) => {
      const newHistory = [...state.aiChatHistory, msg];
      // Debounced save to storage
      setTimeout(() => {
        StorageService.saveAIChatHistory(newHistory).catch((err) => {
          console.error("Failed to save chat history:", err);
        });
      }, 500);
      return { aiChatHistory: newHistory };
    });
  },

  clearChatHistory: () => {
    set({ aiChatHistory: [] });
    StorageService.saveAIChatHistory([]).catch((err) => {
      console.error("Failed to clear chat history:", err);
    });
  },

  addActionHistory: (action, result) => {
    set((state) => {
      const newHistory = [...state.aiActionHistory, { action, executedAt: Date.now(), result }];
      // Debounced save to storage
      setTimeout(() => {
        StorageService.saveAIActionHistory(newHistory).catch((err) => {
          console.error("Failed to save action history:", err);
        });
      }, 500);
      return { aiActionHistory: newHistory };
    });
  },

  computeMaintenanceDue: async (opts = {}) => {
    const now = opts.nowMs ?? Date.now();
    const ttl = opts.ttlMs ?? 5 * 60 * 1000;
    const lastComputed = get().maintenanceDueListLastComputed ?? 0;
    if (!opts.force && lastComputed && now - lastComputed < ttl) {
      set({ maintenanceDueListStatus: "ready" });
      return;
    }
    set({ maintenanceDueListStatus: "computing", maintenanceDueListError: undefined });
    try {
      const dueList = computeDueList(get().quotes, { todayYYYYMMDD: opts.todayYYYYMMDD });
      set({
        maintenanceDueList: dueList,
        maintenanceDueListStatus: "ready",
        maintenanceDueListLastComputed: now,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to compute maintenance due list";
      set({
        maintenanceDueListStatus: "error",
        maintenanceDueListError: message,
      });
      throw error;
    }
  },

  refreshMaintenanceDue: () => get().computeMaintenanceDue({ force: true }),
  seedMaintenanceDueData: (items) => {
    if (!import.meta.env.E2E) return;
    set({
      maintenanceDueList: items,
      maintenanceDueListStatus: "ready",
      maintenanceDueListError: undefined,
      maintenanceDueListLastComputed: Date.now(),
    });
  },
}));
